import { Badge } from "@chakra-ui/react";
import React from "react";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { cafStockApi } from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import { DTE_TIPO_FILTER_SELECT_OPTIONS } from "utils/cafXmlPreview";
import ColumnSortHeader from "./ColumnSortHeader";
import ServerTablePagination from "./ServerTablePagination";

const columnHelper = createColumnHelper();

function formatDate(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function truncateMiddle(s, left = 6, right = 4) {
  if (s == null || s === "") return "—";
  const str = String(s);
  if (str.length <= left + right + 1) return str;
  return `${str.slice(0, left)}…${str.slice(-right)}`;
}

const ESTADO_BADGE = {
  draft: "gray",
  active: "green",
  exhausted: "orange",
  disabled: "red",
};

const FILTERS_INITIAL = () => ({
  rut_emisor: "",
  tipo_dte: "",
  estado: "",
  ambiente: "",
});

const AMBIENTE_OPTS = [
  { value: "", label: "Todos" },
  { value: "certificacion", label: "Certificación" },
  { value: "produccion", label: "Producción" },
];

const ESTADO_OPTS = [
  { value: "", label: "Todos" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "exhausted", label: "Exhausted" },
  { value: "disabled", label: "Disabled" },
];

const PAGE_SIZES = [25, 50, 100];

export default function CafStockTable() {
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedProfileUuid =
    emission.kind === "ready" && emission.profileUuid ? emission.profileUuid : "";
  const [filters, setFilters] = React.useState(FILTERS_INITIAL);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);
  const [sortBy, setSortBy] = React.useState("id");
  const [sortDir, setSortDir] = React.useState("desc");

  const [rawItems, setRawItems] = React.useState([]);
  const [serverTotal, setServerTotal] = React.useState(0);
  const [serverTotalPages, setServerTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const applyColumnSort = React.useCallback((key, dir) => {
    setSortBy(key);
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchList = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setLoading(false);
      setRawItems([]);
      setServerTotal(0);
      setServerTotalPages(0);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      const rut = String(filters.rut_emisor ?? "").trim();
      if (rut) qs.set("rut_emisor", rut);
      const tipo = String(filters.tipo_dte ?? "").trim();
      if (tipo !== "") qs.set("tipo_dte", tipo);
      const est = String(filters.estado ?? "").trim();
      if (est) qs.set("estado", est);
      const amb = String(selectedAmbiente).trim();
      if (amb) qs.set("ambiente", amb);
      if (selectedProfileUuid) qs.set("profileUuid", String(selectedProfileUuid).trim());

      qs.set("limit", String(pageSize));
      qs.set("offset", String((Math.max(1, page) - 1) * pageSize));
      qs.set("sort_by", sortBy);
      qs.set("sort_dir", sortDir);

      const q = qs.toString();
      const { res, data } = await cafStockApi.list(q ? `?${q}` : "");
      if (!res.ok || !data?.ok) {
        setError(data?.error || data?.message || `Error ${res.status}`);
        setRawItems([]);
        setServerTotal(0);
        setServerTotalPages(0);
        return;
      }
      setRawItems(Array.isArray(data.items) ? data.items : []);
      const t = Number(data.total ?? 0);
      setServerTotal(t);
      const tp =
        data.total_pages != null
          ? Number(data.total_pages)
          : Math.ceil(t / pageSize) || 0;
      setServerTotalPages(tp);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red.");
      setRawItems([]);
      setServerTotal(0);
      setServerTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, sortBy, sortDir, selectedAmbiente, selectedProfileUuid]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  React.useEffect(() => {
    const handleUploaded = () => fetchList();
    window.addEventListener("caf-uploaded", handleUploaded);
    return () => window.removeEventListener("caf-uploaded", handleUploaded);
  }, [fetchList]);

  React.useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  React.useEffect(() => {
    if (!selectedAmbiente) return;
    setFilters((f) => {
      if (f.ambiente === selectedAmbiente) return f;
      return { ...f, ambiente: selectedAmbiente };
    });
  }, [selectedAmbiente]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("id", {
        header: () => (
          <ColumnSortHeader
            label="ID"
            sortKey="id"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm font-medium text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("tipo_dte", {
        header: () => (
          <ColumnSortHeader
            label="Tipo"
            sortKey="tipo_dte"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("rut_emisor", {
        header: () => (
          <ColumnSortHeader
            label="RUT emisor"
            sortKey="rut_emisor"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("folio_desde", {
        header: () => (
          <ColumnSortHeader
            label="Desde"
            sortKey="folio_desde"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm tabular-nums text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("folio_hasta", {
        header: () => (
          <ColumnSortHeader
            label="Hasta"
            sortKey="folio_hasta"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm tabular-nums text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("estado", {
        header: () => (
          <ColumnSortHeader
            label="Estado"
            sortKey="estado"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => {
          const v = info.getValue();
          if (v == null || v === "") return "—";
          const key = String(v).toLowerCase();
          return (
            <Badge
              colorScheme={ESTADO_BADGE[key] || "gray"}
              variant="subtle"
              textTransform="uppercase"
              fontSize="0.65rem"
            >
              {String(v)}
            </Badge>
          );
        },
      }),
      columnHelper.accessor("ambiente", {
        header: () => (
          <ColumnSortHeader
            label="Ambiente"
            sortKey="ambiente"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("updated_at", {
        header: () => (
          <ColumnSortHeader
            label="Actualización"
            sortKey="updated_at"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {formatDate(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("caf_uuid", {
        header: () => (
          <ColumnSortHeader
            label="CAF UUID"
            sortKey="caf_uuid"
            activeSortBy={sortBy}
            activeSortDir={sortDir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span
            className="font-mono text-xs text-navy-600 dark:text-gray-300"
            title={info.getValue() != null ? String(info.getValue()) : undefined}
          >
            {truncateMiddle(info.getValue())}
          </span>
        ),
      }),
    ],
    [applyColumnSort, sortBy, sortDir]
  );

  const showBlockingLoading = loading && rawItems.length === 0;

  const table = useReactTable({
    data: rawItems,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id ?? row.caf_uuid),
  });

  return (
    <Card extra="w-full px-6 pb-6 sm:overflow-x-auto">
      <div className="relative flex flex-col gap-3 pt-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            CAF stock
          </div>
          <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
            <code className="rounded bg-lightPrimary px-1 text-[11px] dark:bg-navy-800">
              GET /api/v1/caf-stock
            </code>
            Filtros se envían al servidor. Paginación y ordenación vía query (limit,
            offset, sort_by).
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchList()}
          disabled={loading}
          className="linear shrink-0 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60 dark:bg-brand-400"
        >
          {loading ? "Actualizando…" : "Actualizar"}
        </button>
      </div>

      {showBlockingLoading ? (
        <p className="mt-6 text-sm text-gray-600 dark:text-gray-400">Cargando…</p>
      ) : null}
      {error ? (
        <p
          className="mt-6 rounded-lg border border-red-400/40 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {!error && !showBlockingLoading ? (
        <>
          <div className="mt-6 rounded-xl border border-gray-200 bg-lightPrimary/50 p-4 dark:border-white/10 dark:bg-navy-900/30">
            <div className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
              Filtros
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                RUT emisor
                <input
                  type="text"
                  value={filters.rut_emisor}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, rut_emisor: e.target.value }))
                  }
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  placeholder="Opcional"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                Tipo DTE
                <select
                  value={filters.tipo_dte}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, tipo_dte: e.target.value }))
                  }
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  <option value="">Cualquiera</option>
                  {DTE_TIPO_FILTER_SELECT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                Estado
                <select
                  value={filters.estado}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, estado: e.target.value }))
                  }
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {ESTADO_OPTS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                Ambiente
                <select
                  value={filters.ambiente}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, ambiente: e.target.value }))
                  }
                  disabled
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {AMBIENTE_OPTS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                Filas por página
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="relative mt-4 overflow-x-auto">
            {loading && rawItems.length > 0 ? (
              <div className="pointer-events-none absolute inset-0 z-[1] flex justify-center bg-white/40 pt-6 dark:bg-navy-950/50">
                <span className="h-fit rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium shadow-sm dark:border-white/10 dark:bg-navy-800">
                  Actualizando…
                </span>
              </div>
            ) : null}
            <table className="w-full min-w-[960px]">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr
                    key={hg.id}
                    className="border-b border-gray-200 bg-gray-50/90 dark:border-white/10 dark:bg-navy-900/50"
                  >
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        className="cursor-default select-none px-1 pb-2.5 pt-2.5 text-start align-middle first:pl-0"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="py-8 text-center text-sm text-gray-500"
                    >
                      No hay CAFs con estos filtros.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 dark:border-white/5"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-1 py-3 pr-3 align-middle sm:pr-4"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <ServerTablePagination
            page={page}
            totalPages={serverTotalPages}
            total={serverTotal}
            pageSize={pageSize}
            noun="registros CAF"
            subtext="Ordenación en servidor (parámetros sort_by y sort_dir)."
            onPageChange={setPage}
          />
        </>
      ) : null}
    </Card>
  );
}
